import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import KeyRoundedIcon from "@mui/icons-material/KeyRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import { requestProlongation } from "../../actions";
import AppPage from "../app/AppPage";
import { brand } from "../../theme/businessFlowsTheme";
import { Hint, Refusal, quietDanger } from "../workspaces/WorkspaceUi";
import useBranding, { mailto } from "../../utils/branding";
import { SHOW_PLANS } from "../../features";
import readFeatures from "../../utils/featureFlags";

// Read only behind SHOW_PLANS (frontend/src/features.js): the plan is a
// billing matter between bpExperts and the customer, so a reader doesn't meet
// its name. What they can use is still listed below, in plain words.
const PLAN_NAMES = { trial: "Trial", ultimate: "Ultimate", ultimatePlus: "Ultimate Plus", expired: "Expired" };
const STATUS_LABELS = {
  trialing: "Trial",
  active: "Active",
  past_due: "Payment overdue",
  canceled: "Canceled",
  expired: "Expired",
};
// The two statuses that said when access ends, rather than how it is paid
// for. Retired 28 Sep 2026 with the plan wording: plans decide nothing, so
// "Trial" on this page announced an ending nobody was going to enforce, and
// the chip is behind SHOW_PLANS with every other status now. Kept as a note
// of what the distinction was.
// const TIME_LIMITED = ["trialing", "expired"];
const FLOWOS_SERVICES = { trends: "Market trends", debate: "Process debate", bpmbrain: "Process analysis" };

const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
};

const initials = (user) =>
  [user.firstName, user.lastName]
    .filter(Boolean)
    .map((n) => n.trim()[0])
    .join("")
    .toUpperCase() || (user.email || "?")[0].toUpperCase();

function Section({ title, description, children, sx }) {
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 2.75 }, borderRadius: 4, ...sx }}>
      <Typography variant="h6" component="h2" sx={{ fontSize: "1.05rem" }}>
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {description}
        </Typography>
      )}
      <Box sx={{ mt: 2 }}>{children}</Box>
    </Paper>
  );
}

function DetailRow({ label, value }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, overflowWrap: "anywhere" }}>
        {value || <Box component="span" sx={{ color: "text.disabled" }}>Not provided</Box>}
      </Typography>
    </Box>
  );
}

function Included({ ok, children }) {
  return (
    <Stack direction="row" spacing={1.25} alignItems="center">
      <Box
        sx={{
          width: 22,
          height: 22,
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
          bgcolor: ok ? brand.mark : "action.hover",
          color: ok ? brand.ink : "text.disabled",
        }}
      >
        {ok ? <CheckRoundedIcon sx={{ fontSize: 15 }} /> : <CloseRoundedIcon sx={{ fontSize: 15 }} />}
      </Box>
      <Typography variant="body2" sx={{ color: ok ? "text.primary" : "text.disabled" }}>
        {children}
      </Typography>
    </Stack>
  );
}

export default function MyAccount({ user = {} }) {
  const branding = useBranding();
  const [extension, setExtension] = useState({ state: "idle", msg: "" });

  const plan = user.plan || user.subscription;
  const planName = PLAN_NAMES[plan] || plan || "No plan";
  const status = user.planStatus;
  const ent = user.entitlements || {};
  const hasEntitlements = Object.keys(ent).length > 0;
  const fromOrganisation = user.planSource === "organisation";
  const flowosServices = (ent["flowos.services"] || []).map((s) => FLOWOS_SERVICES[s] || s);
  const features = readFeatures();
  const memberSince = formatDate(user.registrationDate);
  // "Access until" and "Request more time" belong to the plan model and go
  // with it (frontend/src/features.js SHOW_PLANS). A workspace that is given
  // an explicit access-until date of its own (see
  // docs/superpowers/plans/2026-09-28-retiring-subscriptions.md) is what
  // should be shown here instead - that date is the workspace's, not a
  // personal trial's.
  const accessUntil = SHOW_PLANS && !fromOrganisation ? formatDate(user.expirationDate) : null;
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email;

  const requestExtension = async () => {
    setExtension({ state: "sending", msg: "" });
    const res = await requestProlongation(user.email);
    setExtension({
      state: res.success ? "sent" : "failed",
      msg: res.success ? res.msg || "Request sent. We'll email you." : "The request didn't go through. Try again later.",
    });
  };

  return (
    <AppPage>
      {/* Identity */}
      <Paper
        sx={{
          p: { xs: 2.5, md: 3 },
          mb: 2.5,
          borderRadius: 4,
          color: "#fff",
          position: "relative",
          overflow: "hidden",
          bgcolor: brand.inkDeep,
          backgroundImage:
            "radial-gradient(50% 120% at 100% 0%, rgba(240,230,49,0.18), transparent 70%), radial-gradient(60% 120% at 0% 100%, rgba(84,94,214,0.3), transparent 70%)",
        }}
        elevation={0}
      >
        <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 2, sm: 3 }} alignItems={{ sm: "center" }}>
          <Avatar
            sx={{
              width: { xs: 52, md: 60 },
              height: { xs: 52, md: 60 },
              bgcolor: brand.signal,
              color: brand.ink,
              fontWeight: 700,
              fontSize: { xs: "1.3rem", md: "1.6rem" },
            }}
          >
            {initials(user)}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h5" component="p" sx={{ color: "#fff", overflowWrap: "anywhere" }}>
              {fullName}
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.72)", mt: 0.25, overflowWrap: "anywhere" }}>
              {[user.position, user.organisation?.name || user.company].filter(Boolean).join(", ") || user.email}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: "wrap", rowGap: 1 }}>
              {SHOW_PLANS && (
                <Chip
                  size="small"
                  icon={<AutoAwesomeRoundedIcon />}
                  label={planName}
                  sx={{ bgcolor: brand.signal, color: brand.ink, fontWeight: 600, "& .MuiChip-icon": { color: brand.ink } }}
                />
              )}
              {/* Every one of these is a billing word now, trial included:
                  nothing on this page ends an account any more. */}
              {SHOW_PLANS && status && status !== "active" && (
                <Chip
                  size="small"
                  label={STATUS_LABELS[status] || status}
                  sx={{ bgcolor: "rgba(255,255,255,0.12)", color: "#fff" }}
                />
              )}
              {user.role === "admin" && (
                <Chip size="small" label="Administrator" sx={{ bgcolor: "rgba(255,255,255,0.12)", color: "#fff" }} />
              )}
            </Stack>
          </Box>
          <Button
            component={RouterLink}
            to="/changepassword"
            variant="contained"
            className="bf-btn-light"
            startIcon={<KeyRoundedIcon />}
            sx={{
              bgcolor: "#fff",
              color: brand.ink,
              alignSelf: { xs: "stretch", sm: "center" },
              "&:hover": { bgcolor: "rgba(255,255,255,0.88)" },
            }}
          >
            Change password
          </Button>
        </Stack>
      </Paper>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.4fr) minmax(0, 1fr)" },
          gap: 2.5,
          alignItems: "start",
        }}
      >
        <Stack spacing={2.5}>
          <Section title="Profile" description="To change these details, contact your administrator.">
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            <DetailRow label="First name" value={user.firstName} />
            <DetailRow label="Last name" value={user.lastName} />
            <DetailRow label="Email" value={user.email} />
            <DetailRow label="Phone" value={user.phone} />
            <DetailRow label="Role" value={user.position} />
            <DetailRow label="Company" value={user.company} />
            </Box>
          </Section>

          <Section title="Account">
            <Stack spacing={1.5}>
              {memberSince && (
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <EventRoundedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                  <Typography variant="body2">Member since {memberSince}</Typography>
                </Stack>
              )}
              {accessUntil && (
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <EventRoundedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                  <Typography variant="body2">Access until {accessUntil}</Typography>
                </Stack>
              )}
            </Stack>

            {extension.state === "sent" && <Hint sx={{ mt: 2 }}>{extension.msg}</Hint>}
            {extension.state === "failed" && <Refusal sx={{ mt: 2 }}>{extension.msg}</Refusal>}

            <Divider sx={{ my: 2 }} />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              {SHOW_PLANS && !fromOrganisation && (
                <LoadingButton
                  variant="outlined"
                  loading={extension.state === "sending"}
                  disabled={extension.state === "sent"}
                  onClick={requestExtension}
                  startIcon={<EventRoundedIcon />}
                >
                  Request more time
                </LoadingButton>
              )}
              <Button
                href={mailto(branding.supportEmail, "Request for deletion of my account", branding.contactBcc)}
                variant="outlined"
                startIcon={<MailOutlineRoundedIcon />}
                sx={quietDanger}
              >
                Request account deletion
              </Button>
            </Stack>
          </Section>
        </Stack>

        {/* What a reader may use, without naming the plan it comes from: the
            plan name, its status and the upgrade offer are behind SHOW_PLANS
            (frontend/src/features.js), the list of what's included is not. */}
        <Section
          title={SHOW_PLANS ? "Your plan" : "What you can use"}
          description={
            fromOrganisation && user.organisation?.name
              ? `Provided by ${user.organisation.name}.`
              : SHOW_PLANS
              ? "What your subscription includes."
              : "What's switched on for your account."
          }
        >
          {SHOW_PLANS && (
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 3,
                  bgcolor: "primary.main",
                  color: "secondary.main",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                {fromOrganisation ? <BusinessRoundedIcon /> : <AutoAwesomeRoundedIcon />}
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: "1.15rem" }}>{planName}</Typography>
                {status && (
                  <Typography variant="body2" color="text.secondary">
                    {STATUS_LABELS[status] || status}
                    {user.seats ? `, ${user.seats} seats` : ""}
                  </Typography>
                )}
              </Box>
            </Stack>
          )}

          {hasEntitlements ? (
            <Stack spacing={1}>
              <Included ok={ent["content.scope"] !== "sample"}>
                {ent["content.scope"] === "sample" ? "Sample reference processes" : "Full reference process library"}
              </Included>
              <Included ok={Boolean(ent["feature.relationshipExplorer"])}>Relationship Explorer</Included>
              <Included ok={Boolean(ent["feature.advancedSearch"])}>Advanced search</Included>
              <Included ok={Boolean(ent["export.bpmn"] || ent["export.svg"])}>Export BPMN and SVG</Included>
              <Included ok={Boolean(ent["import.bpmn"])}>Import BPMN</Included>
              {/* Named only where this instance offers it (Console → Features).
                  The grant itself is untouched and reads again the day FlowOS
                  is switched back on. */}
              {features.flowos && (
                <Included ok={flowosServices.length > 0}>
                  FlowOS AI{flowosServices.length ? `: ${flowosServices.join(", ")}` : ""}
                </Included>
              )}
              <Included ok={!ent["feature.watermark"]}>Diagrams without watermark</Included>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {SHOW_PLANS ? "Log out and back in to see what your plan includes." : "Log out and back in to see what's switched on."}
            </Typography>
          )}

          {SHOW_PLANS && plan !== "ultimatePlus" && (
            <Button
              href={mailto(branding.supportEmail, `Upgrade my ${branding.productName} plan`)}
              variant="contained"
              fullWidth
              sx={{ mt: 2.5 }}
            >
              Talk to us about upgrading
            </Button>
          )}
        </Section>
      </Box>
    </AppPage>
  );
}
